import {
  computeTeamSummaries,
  missingChipsByTeam,
  type ChipDto,
} from '@pietrochip/shared';
import { useId, useMemo, useState } from 'react';
import { personName, percent } from '../lib/format';
import { EmptyState, ProgressBar } from './ui';

/**
 * Quanto manca a ogni società.
 *
 * È la domanda che al banco si fa per telefono — «quanti ne avete ancora voi?» —
 * e senza questa sezione la risposta richiedeva di scorrere l'elenco dei
 * mancanti contandoli a mano. La ripartizione si calcola **dai chip**, che il
 * client ha già: nessuna chiamata, nessun conteggio che possa restare indietro
 * rispetto all'elenco che si ha davanti.
 *
 * Le due liste — i conteggi e i mancanti di una squadra — escono dalla stessa
 * normalizzazione (`computeTeamSummaries` e `missingChipsByTeam`), ed è per
 * questo che vivono nella stessa funzione: se divergessero, aprire una squadra
 * mostrerebbe i mancanti di un'altra.
 */
export function TeamProgress({ chips }: { chips: readonly ChipDto[] }) {
  const [selected, setSelected] = useState<string | null>(null);
  const panelId = useId();

  const summaries = useMemo(() => computeTeamSummaries(chips), [chips]);
  const missing = useMemo(() => missingChipsByTeam(chips), [chips]);

  /**
   * La squadra aperta, se esiste ancora.
   *
   * Con i chip che si muovono, la squadra aperta può sparire — l'ultimo chip
   * rimosso, o un evento diverso. In quel caso il pannello non si disegna: una
   * tendina aperta su niente è peggio di una chiusa, perché sembra che i dati
   * non siano arrivati.
   */
  const openTeam =
    selected !== null && summaries.some((summary) => summary.team === selected) ? selected : null;
  const openMissing = openTeam === null ? undefined : missing.get(openTeam);

  if (summaries.length === 0) {
    return (
      <EmptyState
        title="Nessun chip in questo evento"
        hint="Importa l’elenco dei transponder: da lì in poi la dashboard dice quanto manca a ogni società."
      />
    );
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="teams">
        {summaries.map((summary) => {
          const open = summary.team === openTeam;

          return (
            <button
              key={summary.team}
              type="button"
              className="team-tile"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setSelected(open ? null : summary.team)}
            >
              <span className="team-tile__name">{summary.team}</span>
              <span className="team-tile__counts">
                {summary.returned}/{summary.total} · {percent(summary.percent, 0)}
              </span>
              <ProgressBar fraction={summary.percent} label={`Restituiti per ${summary.team}`} />
              <span className="team-tile__caret" aria-hidden="true">
                {open ? '▾' : '▸'}
              </span>
            </button>
          );
        })}
      </div>

      {openTeam !== null && (
        <section className="team-missing" id={panelId} aria-label={`Chip mancanti di ${openTeam}`}>
          {openMissing === undefined || openMissing.length === 0 ? (
            <p className="team-missing__done">
              <strong>{openTeam}</strong> ha restituito tutti i suoi transponder.
            </p>
          ) : (
            <>
              <div className="team-missing__head">
                <span>{openMissing.length} da restituire</span>
                <span className="faint">in ordine di cognome</span>
              </div>
              <ul className="team-missing__list">
                {openMissing.map((chip) => (
                  <li key={chip.id} className="team-missing__item">
                    <span className="mono team-missing__code">{chip.code}</span>
                    <span className="team-missing__who">
                      {personName(chip.firstName, chip.lastName)}
                    </span>
                    <span className="faint team-missing__number">
                      {chip.raceNumber ? `n. ${chip.raceNumber}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}
